import { inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, of, Subject, Subscription } from 'rxjs';
import { delay, filter, map, take } from 'rxjs/operators';

import { RoleType, User } from 'src/app/shared/models';
import { HeaderTypeEnum, NotificationTypeEnum } from 'src/app/shared/enums';
import { NAVIGATOR } from 'src/app/core/tokens';
import { NotificationService } from 'src/app/shared/services/helpers';
import { UserService } from 'src/app/shared/services/requests';
import { CookiesService, StorageService } from 'src/app/core/services/global';
import { ScrollHandlerService } from 'src/app/shared/services/scroll';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private navigator = inject(NAVIGATOR);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private userService = inject(UserService);
  private storageService = inject(StorageService);
  private scrollHandler = inject(ScrollHandlerService);
  private cookie = inject(CookiesService);

  private accessToken: string = this.storageService.getAccessToken();
  private refreshToken: string = this.storageService.getRefreshToken();
  private jwtService: JwtHelperService = new JwtHelperService();
  private isTokenRefreshing: boolean;

  private requestQueue: BehaviorSubject<boolean>[] = [];
  private onLogoutSubject = new Subject();
  private isThatTabDoRefreshing: boolean;

  onLogout$ = this.onLogoutSubject.asObservable();

  private logout$: Subscription;
  private logoutDelay$: Subscription;

  isAdmin$ = this.userService.currentUser$.pipe(
    filter((user: User) => !!user),
    map((user: User) => user.role === 'ROLE_ADMIN'),
  );
  isSuperAdmin$ = new BehaviorSubject<boolean>(false);

  // simple get utils:

  getAccessToken(): string {
    this.loadActualAccessToken();

    return this.accessToken;
  }

  getRefreshToken(): string {
    this.loadActualRefreshToken();

    return this.refreshToken;
  }

  getUserId(): string {
    this.loadActualAccessToken();

    return this.jwtService.decodeToken(this.accessToken)?.userId;
  }

  getIsUserLoggedIn(): boolean {
    this.loadActualAccessToken();

    return !!this.accessToken;
  }

  getIsThatTabDoRefreshing(): boolean {
    return this.isThatTabDoRefreshing;
  }

  getIsAccessTokenRefreshing(): boolean {
    return this.isTokenRefreshing;
  }

  getIsAdmin(): boolean {
    let isAdmin = false;

    const user = this.userService.getUser();
    if (user) {
      const role: RoleType = user.role;
      isAdmin = role === 'ROLE_ADMIN';
    }

    return isAdmin;
  }

  getIsSuperAdmin(): boolean {
    let isSuperAdmin = false;

    if (this.accessToken) {
      const decodedToken = this.jwtService.decodeToken(this.accessToken);
      isSuperAdmin = decodedToken?.sa;
    }

    return isSuperAdmin;
  }

  isOnline(): boolean {
    if (this.navigator?.onLine !== undefined) {
      return this.navigator.onLine;
    }

    return true;
  }

  // simple void utils:

  setAccessToken(accessToken: string): void {
    this.accessToken = accessToken;

    this.storageService.setAccessToken(accessToken);
  }

  saveRefreshToken(refreshToken: string): void {
    this.refreshToken = refreshToken;

    this.storageService.setRefreshToken(refreshToken);
  }

  loadActualRefreshToken(): void {
    this.refreshToken = this.storageService.getRefreshToken();
  }

  loadActualAccessToken(): void {
    this.accessToken = this.storageService.getAccessToken();

    this.launchRolesAtToken();
  }

  startTokenRefreshing(): void {
    this.isTokenRefreshing = true;
    this.isThatTabDoRefreshing = true;
  }

  stopTokenRefreshing(): void {
    this.isTokenRefreshing = false;
  }

  resetRequestsQueue(): void {
    if (this.requestQueue?.length) {
      this.requestQueue = [];
    }
  }

  pushItemAtRequestsQueue(subject: BehaviorSubject<boolean>): void {
    if (subject) {
      this.requestQueue.push(subject);
    }
  }

  // handlers:

  handleDataAfterRefresh(data: HttpResponse<User>, withUser: boolean = true): void {
    const newAccessToken: string = data.headers?.get(HeaderTypeEnum.JWT_TOKEN);
    const newRefreshToken: string = data.headers?.get(HeaderTypeEnum.JWT_REFRESH_TOKEN);
    const newUser: User = withUser ? data.body : null;

    if (newAccessToken && newRefreshToken) {
      this.saveDataAfterRefresh(newAccessToken, newRefreshToken, newUser);
    }
  }

  fullUserLogout(
    withMessage: boolean = true,
    errorMessage: string = 'NOTIFICATION.YOU_NEED_TO_LOGIN_AGAIN',
  ): void {
    const isNotAlreadyLogout: boolean = this.clearSession();

    this.scrollHandler.scrollTo(0, 'auto');
    this.logoutWithNavigate(isNotAlreadyLogout, withMessage, errorMessage);
  }

  // Logs out on the backend and clears local session data without navigating. Returns false if already logged out.
  clearSession(): boolean {
    this.loadActualAccessToken();
    const isNotAlreadyLogout: boolean = !!this.accessToken;

    if (isNotAlreadyLogout) {
      this.logout$ = this.userService.logout().pipe(take(1)).subscribe();
    }

    this.accessToken = null;
    this.storageService.clear();
    this.cookie.clearCookieBeforeLogout();

    return isNotAlreadyLogout;
  }

  saveDataAfterRefresh(accessToken: string, refreshToken: string, userNew: User): void {
    this.setAccessToken(accessToken);
    this.saveRefreshToken(refreshToken);

    if (userNew) {
      this.userService.updateUser(userNew);
    }
  }

  handleRefreshingOnDestroy(): void {
    const isThatTabDoRefreshing: boolean = this.getIsThatTabDoRefreshing();
    const isAccessTokenRefreshing: boolean = this.getIsAccessTokenRefreshing();

    if (isThatTabDoRefreshing && isAccessTokenRefreshing) {
      this.stopTokenRefreshing();
    }
  }

  handleHeldRequests(): void {
    if (this.getAccessToken()) {
      while (this.requestQueue?.length) {
        this.requestQueue.shift().next(true);
      }
    }
  }

  sendOnLogout(): void {
    this.onLogoutSubject.next(null);
  }

  unsubscribe(): void {
    if (this.logout$) {
      this.logout$.unsubscribe();
    }

    if (this.logoutDelay$) {
      this.logoutDelay$.unsubscribe();
    }
  }

  private logoutWithNavigate(
    isNotAlreadyLogout: boolean,
    withMessage: boolean,
    errorMessage: string,
  ): void {
    this.router.navigateByUrl('/').then(() => {
      this.sendOnLogout();

      if (isNotAlreadyLogout && withMessage) {
        const logoutDelay = 200;

        this.logoutDelay$ = of(null)
          .pipe(delay(logoutDelay), take(1))
          .subscribe(() => {
            this.notificationService.notify(NotificationTypeEnum.ERROR, errorMessage);
          });
      }
    });
  }

  private launchRolesAtToken(): void {
    let isSuperAdmin = this.getIsSuperAdmin();

    this.isSuperAdmin$.next(isSuperAdmin);
  }
}
